import { useState } from "react";
import { waplifyChatHref, type PendingSupportChat } from "./api";
import { formatWaiting, type Severity } from "./priority";
import { dangerBtn, ghostBtn } from "./ui";

/**
 * A pending WhatsApp support chat — a parent's question that picard's escalation counts
 * before it pings the doctor group and CALLS the support hotline. This lane exists so
 * the team, when the phone rings, can SEE which questions are pending instead of being
 * called blind.
 *
 * These are answered by DOCTORS in the app, not by a CM over WhatsApp — so the card is
 * visibility + a link to READ the chat context in waplify. It never sends.
 *
 * The one write it does have is "Not a question": on a community activity day this lane
 * fills with bingo entries and congratulations from parents whose chat was doctor-assigned
 * by some earlier real question, and every one of them re-arms the SLA. Closing stops the
 * clock for that chat. It does NOT reply — picard opens a fresh chat if the parent writes
 * again.
 */

// Escalation thresholds (picard SUPPORT_SLA_*): >=25 min the hotline is being called,
// >=15 min the doctor group is being pinged. Colour the wait accordingly.
function severityFor(waitMin: number): Severity {
  if (waitMin >= 25) return "critical";
  if (waitMin >= 15) return "warning";
  return "muted";
}

const rail: Record<Severity, string> = {
  critical: "border-l-4 ![border-left-color:#dc2626]",
  warning: "border-l-4 ![border-left-color:#f59e0b]",
  muted:
    "border-l-4 ![border-left-color:#cbd5e1] dark:![border-left-color:#334155]",
};

const wash: Record<Severity, string> = {
  critical:
    "bg-crit-50 hover:bg-crit-100 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  warning:
    "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
  muted:
    "bg-white hover:bg-clinic-50 dark:!bg-navy-800 dark:hover:!bg-navy-700",
};

const urgencyTone: Record<Severity, string> = {
  critical: "text-crit-600 dark:text-red-400",
  warning: "text-warn-600 dark:text-amber-400",
  muted: "text-clinic-500 dark:text-gray-400",
};

interface Props {
  chat: PendingSupportChat;
  /**
   * Close this chat because it isn't a question. NOT a reply — it flips the chat closed
   * so it stops counting toward the doctor-group ping and the hotline call.
   */
  onClose: (chat: PendingSupportChat) => void;
  pending: boolean;
  error: string | null;
}

export default function SupportChatCard({
  chat,
  onClose,
  pending,
  error,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const severity = severityFor(chat.wait_minutes);
  const waHref = chat.user_mobile ? waplifyChatHref(chat.user_mobile) : null;
  const calling = chat.wait_minutes >= 25;

  return (
    <li
      data-testid="support-chat-card"
      data-chat={chat.chat_id}
      className={`flex items-center gap-4 px-4 py-3 ${rail[severity]} ${wash[severity]}`}
    >
      {/* scan anchor: the wait, the one number that says "how bad" */}
      <div className="w-14 shrink-0 text-center">
        <div
          className={`text-[15px] font-bold tabular-nums leading-none ${urgencyTone[severity]}`}
        >
          {formatWaiting(chat.wait_minutes)}
        </div>
        <div className="mt-1 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-clinic-400 dark:text-gray-500">
          waiting
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[13px] font-semibold leading-tight text-clinic-900 dark:text-white">
            {chat.user_name || "Unknown parent"}
          </span>
          {chat.user_mobile && (
            <span className="text-xs tabular-nums text-clinic-500 dark:text-gray-400">
              {chat.user_mobile}
            </span>
          )}
          {calling && (
            <span className="rounded bg-crit-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight tracking-wide text-white">
              calling support
            </span>
          )}
        </div>
        <p className="line-clamp-2 mt-0.5 text-[13px] leading-snug text-clinic-700 dark:text-gray-200">
          {chat.message}
        </p>
        {chat.group_name && (
          <p className="mt-0.5 text-xs text-clinic-400 dark:text-gray-500">
            {chat.group_name} · answered by a doctor in the app
          </p>
        )}

        {error && (
          <p
            role="alert"
            data-testid="support-chat-error"
            className="mt-1.5 rounded bg-crit-100 px-2 py-1 text-xs font-medium text-crit-700 ring-1 ring-crit-200"
          >
            {error} — the chat is unchanged. Retry, or reload the queue.
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-chat-waplify"
            className={ghostBtn}
          >
            Open chat in waplify
          </a>
        )}

        {/*
          Two-step, like the "Parents waiting" dismiss: a stray click must not silently
          drop a real parent question off the top lane. The confirm copy says what it does
          NOT do, because that is the thing a CM needs to be sure of.
        */}
        {confirming ? (
          <span
            role="group"
            aria-label="Confirm close as not a question"
            className="flex items-center gap-1.5 rounded-md bg-warn-50 px-2 py-1 ring-1 ring-warn-200 dark:!bg-navy-900 dark:ring-navy-700"
          >
            <span className="text-xs font-medium text-warn-700 dark:text-amber-200">
              Close (no reply)?
            </span>
            <button
              type="button"
              data-testid="support-chat-close-confirm"
              disabled={pending}
              onClick={() => {
                setConfirming(false);
                onClose(chat);
              }}
              className={dangerBtn}
            >
              {pending ? "Working…" : "Yes, close"}
            </button>
            <button
              type="button"
              data-testid="support-chat-close-cancel"
              onClick={() => setConfirming(false)}
              className={ghostBtn}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            data-testid="support-chat-close"
            disabled={pending}
            title="Close as not a question (does not reply)"
            aria-label="Close as not a question (does not reply)"
            onClick={() => setConfirming(true)}
            className={ghostBtn}
          >
            Not a question
          </button>
        )}
      </div>
    </li>
  );
}
